import React from 'react';
import type ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { Download, FileImage, FileSpreadsheet, FileText } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { type ExportOptions, type ChartExportOptions } from '@/shared/lib/export';

import { useChartExport } from '../model/use-chart-export';

interface ChartExportMenuProps {
  chartRef: React.RefObject<ReactECharts | null>;
  chartOption: EChartsOption | Record<string, unknown>;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  disabled?: boolean;
  className?: string;
}

/**
 * Export menu component for ECharts
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * <ChartExportMenu
 *   chartRef={chartRef}
 *   chartOption={chartOption}
 *   exportOptions={{
 *     png: { filename: 'chart.png', pixelRatio: 2 },
 *     csv: { filename: 'data.csv' }
 *   }}
 * />
 */
export const ChartExportMenu: React.FC<ChartExportMenuProps> = ({
  chartRef,
  chartOption,
  exportOptions,
  disabled = false,
  className
}) => {
  const { t } = useTranslation();
  const { exportPNG, exportCSV, exportXLSX } = useChartExport({ chartOption, exportOptions, chartRef });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className={className}>
          <Download />
          <span className="hidden md:inline">{t('common.export.label', 'Export')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportPNG} data-testid="export-png">
          <FileImage className="h-4 w-4 mr-2" />
          {t('common.export.png', 'Export as PNG')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportCSV} data-testid="export-csv">
          <FileText className="h-4 w-4 mr-2" />
          {t('common.export.csv', 'Export as CSV')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportXLSX} data-testid="export-xlsx">
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {t('common.export.xlsx', 'Export as Excel')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
